import { useCallback, useEffect, useRef, useState } from "react";
import { Link, useOutletContext, useParams } from "react-router-dom";
import { Loader2 } from "lucide-react";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import PageHeader from "../PageHeader";
import AvatarDialog from "../people/AvatarDialog";
import UserAvatar from "../people/UserAvatar";
import { agentApi } from "../../services/api";
import { apiErrorMessage } from "../../services/apiError";
import { relativeTime } from "../tasks/userDisplay";
import { describeEvent } from "../tasks/activityText";

/**
 * One agent: who it is, how alive it is, and what it did — its thread
 * events across every task you can see, newest first, each linking to the
 * task on the board. Actions (rotate / deactivate) stay on the Overview.
 */
function AgentDetail() {
  const { id } = useParams();
  const agentId = Number(id);
  const { me } = useOutletContext() || {};
  const [pictureOpen, setPictureOpen] = useState(false);
  const [agent, setAgent] = useState(null);
  const [agentError, setAgentError] = useState(null);
  const [events, setEvents] = useState([]);
  const [count, setCount] = useState(0);
  const [next, setNext] = useState(null);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(true);
  const [feedError, setFeedError] = useState(null);
  const [failedPage, setFailedPage] = useState(null);
  // Which agent the in-flight requests belong to. A fast back/forward can
  // land agent A's response after agent B's; responses for anyone but the
  // current id are dropped.
  const liveIdRef = useRef(agentId);

  const loadAgent = useCallback(async () => {
    setAgentError(null);
    try {
      const { data } = await agentApi.get(agentId);
      if (liveIdRef.current === agentId) setAgent(data);
    } catch (err) {
      if (liveIdRef.current === agentId) setAgentError(err);
    }
  }, [agentId]);

  const loadPage = useCallback(
    async (pageNumber) => {
      setLoading(true);
      setFeedError(null);
      try {
        const { data } = await agentApi.activity(agentId, { page: pageNumber });
        if (liveIdRef.current !== agentId) return;
        const rows = data.results || [];
        setEvents((prev) => {
          if (pageNumber === 1) return rows;
          // Newest-first pages shift when an event lands mid-scroll; the
          // same id must never render twice.
          const seen = new Set(prev.map((e) => e.id));
          return [...prev, ...rows.filter((e) => !seen.has(e.id))];
        });
        setCount(data.count ?? 0);
        setNext(data.next || null);
        setPage(pageNumber);
        setFailedPage(null);
      } catch (err) {
        if (liveIdRef.current !== agentId) return;
        setFeedError(err);
        setFailedPage(pageNumber);
      } finally {
        if (liveIdRef.current === agentId) setLoading(false);
      }
    },
    [agentId],
  );

  useEffect(() => {
    liveIdRef.current = agentId;
    // A different agent: nothing of the previous one may stay on screen —
    // its picture dialog included (it would reopen for the next agent).
    setPictureOpen(false);
    setAgent(null);
    setAgentError(null);
    setEvents([]);
    setCount(0);
    setNext(null);
    setPage(1);
    setFeedError(null);
    setFailedPage(null);
    if (!Number.isFinite(agentId) || agentId <= 0) {
      setLoading(false);
      return;
    }
    loadAgent();
    loadPage(1);
  }, [agentId, loadAgent, loadPage]);

  if (!Number.isFinite(agentId) || agentId <= 0) {
    return (
      <div className="page-container mt-6 pt-4">
        <Alert variant="destructive">
          <AlertDescription>That isn&apos;t an agent id.</AlertDescription>
        </Alert>
      </div>
    );
  }

  const s = agent?.stats || {};

  return (
    <div className="page-container mt-6 pt-4">
      <div className="mb-2">
        <Link to="/agents">← All agents</Link>
      </div>

      {agentError && (
        <Alert variant="destructive">
          <AlertDescription className="flex items-center gap-3">
            <span className="flex-1">
              {agentError.response?.status === 404
                ? "No such agent."
                : apiErrorMessage(agentError, "Failed to load the agent.")}
            </span>
            {agentError.response?.status !== 404 && (
              <Button
                size="sm"
                variant="outline"
                className="border-destructive/40 text-destructive hover:bg-destructive/10 hover:text-destructive"
                onClick={loadAgent}
                aria-label="Retry loading the agent"
              >
                Retry
              </Button>
            )}
          </AlertDescription>
        </Alert>
      )}

      {agent && (
        <>
          <div className="flex items-start gap-4">
            <div className="mt-6 flex flex-col items-center gap-1">
              <UserAvatar user={agent} size="lg" />
              {/* Staff, or the person this agent works for (spec D7) —
                  the one thing an owner may change here. */}
              {(me?.is_staff || (me && agent.owner?.id === me.id)) && (
                <Button
                  type="button"
                  variant="link"
                  size="sm"
                  className="h-auto p-0 text-xs"
                  onClick={() => setPictureOpen(true)}
                >
                  {agent.avatar_url ? "Change picture" : "Add a picture"}
                </Button>
              )}
            </div>
            <PageHeader
              className="min-w-0 flex-1"
              title={agent.username}
              subtitle={agent.description || "No description yet."}
              badge={agent.is_active ? "agent" : "inactive"}
              badgeVariant={agent.is_active ? "secondary" : "outline"}
            />
          </div>
          <AvatarDialog
            open={pictureOpen}
            onOpenChange={setPictureOpen}
            user={agent}
            title={`${agent.username}'s picture`}
            whose={`${agent.username}'s`}
            onSave={async (file) => {
              const { data } = await agentApi.setAvatar(agent.id, file);
              setAgent((prev) =>
                prev?.id === agent.id
                  ? { ...prev, avatar_url: data.avatar_url }
                  : prev,
              );
            }}
            onRemove={async () => {
              await agentApi.removeAvatar(agent.id);
              setAgent((prev) =>
                prev?.id === agent.id ? { ...prev, avatar_url: null } : prev,
              );
            }}
          />
          <Card className="mb-4 gap-3 py-4">
            <CardContent className="flex flex-wrap gap-4 text-sm text-muted-foreground">
              <span>
                {agent.last_seen_at
                  ? `Last seen ${relativeTime(agent.last_seen_at)}`
                  : "Never seen"}
              </span>
              <span>Created {relativeTime(agent.created_at)}</span>
              <span>
                <strong className="text-foreground">
                  {s.assigned_open ?? 0}
                </strong>{" "}
                open assigned
              </span>
              <span>
                <strong className="text-foreground">
                  {s.claimed_now ?? 0}
                </strong>{" "}
                claimed now
              </span>
              <span>
                <strong className="text-foreground">
                  {s.comments_7d ?? 0}
                </strong>{" "}
                comments this week
              </span>
            </CardContent>
          </Card>
        </>
      )}

      <section aria-label="Activity">
        <h2 className="mb-2 text-sm font-medium tracking-wide text-muted-foreground uppercase">
          Activity{count ? <span className="ml-1">({count})</span> : null}
        </h2>
        <p className="text-sm text-muted-foreground">
          Everything this agent did on tasks you can see, newest first. To
          rotate or deactivate it, use the Overview.
        </p>

        {feedError && (
          <Alert className="py-2">
            <AlertDescription className="flex items-center gap-3 text-sm">
              <span className="flex-1">
                Couldn&apos;t load {failedPage > 1 ? "more of " : ""}the
                activity.
              </span>
              <Button
                size="sm"
                variant="outline"
                onClick={() => loadPage(failedPage || 1)}
                aria-label="Retry loading the activity"
              >
                Retry
              </Button>
            </AlertDescription>
          </Alert>
        )}

        {!loading && !feedError && events.length === 0 && (
          <div className="text-sm italic text-muted-foreground">
            Nothing yet — claims, comments, and status changes will appear here.
          </div>
        )}

        {events.length > 0 && (
          <ul className="mb-2 list-none p-0">
            {events.map((e) => {
              const line = describeEvent(e);
              return (
                <li key={e.id} className="mb-2 text-sm">
                  <div className="text-muted-foreground">
                    {/* The actor is always this agent (the feed is by actor),
                        so the name isn't repeated on every line. */}
                    <span className="text-foreground">
                      {line || (e.verb === "comment" ? "commented" : e.verb)}
                    </span>
                    {" on "}
                    <Link to={`/tasks/board?task=${e.task}`}>
                      {e.task_title || `task ${e.task}`}
                    </Link>
                    <span className="ml-2" title={e.created_at}>
                      {relativeTime(e.created_at)}
                    </span>
                  </div>
                  {e.verb === "comment" && (
                    <div className="mt-1 whitespace-pre-wrap">
                      {e.payload?.body}
                    </div>
                  )}
                </li>
              );
            })}
          </ul>
        )}

        {loading && (
          <div className="text-sm text-muted-foreground">
            <Loader2 className="mr-2 inline size-4 animate-spin" />
            Loading…
          </div>
        )}

        {!loading && !feedError && next && (
          <Button
            size="sm"
            variant="outline"
            onClick={() => loadPage(page + 1)}
          >
            Load more ({count - events.length} older)
          </Button>
        )}
      </section>
    </div>
  );
}

export default AgentDetail;
